import { Button, Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import styles from "./User.module.css";
import FormDialog from "../common/FormDialog";
import { axiosAPIWithJson, axiosGet } from "../../util/axios";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";

import {
    OpenSnackBarWithResultFail,
    OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";
import UserForm from "./UserForm";
import {
    clearInputThunk,
    initUserListThunk,
    selectedEditUserThunk,
} from "../../redux/user/thunk";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { CheckCircle, Cancel, EditNote, BugReport } from "@mui/icons-material";
import moment from "moment";
import { UserFormState, userDataList } from "../../redux/user/state";
import AppLogDialog from "./AppLogDialog";

function User() {
    const [openAddUserDialog, setOpenUserDialog] = useState<boolean>(false);
    const [logDialogUser, setLogDialogUser] = useState<{
        userId: number;
        username: string;
    } | null>(null);

    const dispatch = useDispatch<IRootThunkDispatch>();
    const {
        username,
        password,
        repeatPassword,
        role,
        email,
        truck,
        userList,
        selectedUser,
        status,
        user_id,
    } = useSelector((state: IRootState) => state.userFormState);
    const dataFetchedRef = useRef(false);
    const passwordLengthLimit = 4;

    function handleClickAddUserButton() {
        setOpenUserDialog(true);
    }

    function handleCloseDialog() {
        dispatch(clearInputThunk());
        setOpenUserDialog(false);
    }

    function handleClickEditUserButton(rowData: userDataList) {
        dispatch(selectedEditUserThunk(rowData));
        setOpenUserDialog(true);
    }

    useEffect(() => {
        const controller = new AbortController();
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        InitUserData();
        return () => {
            controller.abort();
        };
    }, []);

    async function InitUserData() {
        const result = await axiosGet("/api/user");
        if (result.data.success) {
            dispatch(initUserListThunk(result.data.userList));
        } else {
            dispatch(OpenSnackBarWithResultFail("未能下載用戶資料"));
        }
    }

    const columns: GridColDef[] = [
        { field: "user_id", headerName: "用戶編號", flex: 1 },
        { field: "username", headerName: "用戶名稱", flex: 1 },
        { field: "role", headerName: "用戶角色", flex: 1 },
        {
            field: "status",
            headerName: "用戶狀態",
            flex: 1,
            renderCell: (data) =>
                data.value === 1 ? (
                    <div>
                        <CheckCircle />
                    </div>
                ) : (
                    <div>
                        <Cancel />
                    </div>
                ),
        },
        {
            field: "create_date",
            headerName: "建立日期",
            flex: 1,
            renderCell: (data) => (
                <div>
                    {moment.utc(data.value).format("YYYY-MM-DD HH:mm:ss")}
                </div>
            ),
        },
        {
            field: "update_date",
            headerName: "更新日期",
            flex: 1,
            renderCell: (data) => (
                <div>
                    {moment.utc(data.value).format("YYYY-MM-DD HH:mm:ss")}
                </div>
            ),
        },
        {
            field: "action",
            headerName: "操作",
            flex: 1.2,
            renderCell: (param) => (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <EditNote
                        style={{ cursor: "pointer" }}
                        onClick={() => handleClickEditUserButton(param.row)}
                    />
                    <BugReport
                        style={{ cursor: "pointer" }}
                        titleAccess="App 日誌"
                        onClick={() =>
                            setLogDialogUser({
                                userId: param.row.user_id,
                                username: param.row.username,
                            })
                        }
                    />
                </div>
            ),
        },
    ];

    async function handleSubmit() {
        let result;

        // for insert new user
        let form: UserFormState = {
            username: username,
            password: password,
            repeatPassword: repeatPassword,
            role: role,
            email: email,
            truck: truck,
        };

        if (selectedUser) {
            //for editing

            form.status = status;
            form.user_id = user_id;

            if (!username || !role) {
                dispatch(OpenSnackBarWithResultFail("未有填寫所有項目"));
                return;
            }
            if (password && password.length < passwordLengthLimit) {
                dispatch(OpenSnackBarWithResultFail("密碼最少四位數"));
                return;
            }

            if (password !== repeatPassword) {
                dispatch(OpenSnackBarWithResultFail("密碼必須一致"));
                return;
            }
            result = await axiosAPIWithJson.put("/api/user", form);
        } else {
            //for insert

            if (!username || !password || !repeatPassword || !role) {
                dispatch(OpenSnackBarWithResultFail("未有填寫所有項目"));
                return;
            }
            if (password !== repeatPassword) {
                dispatch(OpenSnackBarWithResultFail("密碼必須一致"));
                return;
            }
            if (password.length < passwordLengthLimit) {
                dispatch(OpenSnackBarWithResultFail("密碼最少八位數"));
                return;
            }

            result = await axiosAPIWithJson.post("/api/user", form);
        }

        if (result?.data.success) {
            dispatch(OpenSnackBarWithResultSuccess(result.data.message));
            dispatch(clearInputThunk());
            InitUserData();
            setOpenUserDialog(false);
        } else {
            dispatch(OpenSnackBarWithResultFail(result?.data.message));
        }
    }

    return (
        <>
            <div className={styles.user_outer_container}>
                <Grid>用戶資訊</Grid>

                <Grid onClick={handleClickAddUserButton}>
                    <Button variant="contained">新增用戶</Button>
                </Grid>
            </div>

            <FormDialog
                title={selectedUser ? "更新用戶" : "新增用戶"}
                open={openAddUserDialog}
                handleClose={handleCloseDialog}
                handleSubmit={handleSubmit}
                size="xs"
            >
                <UserForm />
            </FormDialog>

            <div style={{ height: "900px", width: "100%" }}>
                <DataGrid
                    rows={userList ? userList : []}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 30 },
                        },
                    }}
                    pageSizeOptions={[5, 10]}
                    checkboxSelection
                    getRowId={(row) => row.user_id}
                />
            </div>

            {logDialogUser ? (
                <AppLogDialog
                    open={!!logDialogUser}
                    userId={logDialogUser.userId}
                    username={logDialogUser.username}
                    onClose={() => setLogDialogUser(null)}
                />
            ) : null}
        </>
    );
}

export default User;
